/**
 * Virtual Cursor for Gaze/Head Tracking (REQ-ARCH-02)
 * 
 * CRITICAL PERFORMANCE: This component reads from CursorStore in a 
 * requestAnimationFrame loop. It does NOT receive props/context for position.
 * This allows 60fps cursor updates without React re-renders.
 * 
 * @requirements REQ-ARCH-01: Cursor position never triggers React re-render
 * @requirements REQ-ARCH-02: Interaction Layer rendered on top z-index
 */

import React, { useRef, useEffect, useState } from 'react';
import { useInputMethod } from '@/contexts/InputMethodContext';
import { InputMethod } from '@/lib/inputMethods';
import { CursorStore } from '@/features/cursor/CursorStore';

export default function VirtualCursor() {
  const { inputMethod, isTrackingActive } = useInputMethod();
  
  // Refs for direct DOM manipulation (no React re-renders for position)
  const containerRef = useRef<HTMLDivElement>(null);
  const progressRingRef = useRef<SVGCircleElement>(null);
  const bgRingRef = useRef<SVGCircleElement>(null);
  const centerDotRef = useRef<HTMLDivElement>(null);
  const animationFrameRef = useRef<number>();
  
  // Only track active state in React (not position - that's transient)
  const [isPaused, setIsPaused] = useState(false);

  // Only show cursor for gaze/head tracking methods
  const isTrackingMethod = 
    inputMethod === InputMethod.GAZE_TRACKING || 
    inputMethod === InputMethod.HEAD_TRACKING;
  
  // Cursor rendering loop - runs at 60fps, bypasses React
  useEffect(() => {
    if (!isTrackingMethod) {
      // Cleanup animation frame when not tracking
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = undefined;
      }
      return;
    }

    const renderLoop = () => {
      if (!containerRef.current) {
        animationFrameRef.current = requestAnimationFrame(renderLoop);
        return;
      }

      const state = CursorStore.getState();
      
      // Direct DOM update - no React involvement
      containerRef.current.style.left = `${state.x}px`;
      containerRef.current.style.top = `${state.y}px`;

      // Detect cursor color override from element under cursor
      // Must hide cursor temporarily so elementFromPoint hits the element beneath
      let elementUnder: HTMLElement | null = null;
      try {
        containerRef.current.style.display = 'none';
        elementUnder = document.elementFromPoint(state.x, state.y) as HTMLElement | null;
        containerRef.current.style.display = '';
      } catch (_) {
        if (containerRef.current) containerRef.current.style.display = '';
      }
      const cursorColorAttr = elementUnder?.closest?.('[data-cursor-color]')?.getAttribute('data-cursor-color');
      const isTeal = cursorColorAttr === 'teal';
      const ringColor = isTeal ? 'hsl(var(--border))' : 'hsl(var(--accent))';

      // Update background ring color + opacity
      if (bgRingRef.current) {
        bgRingRef.current.style.stroke = ringColor;
        bgRingRef.current.style.opacity = isTeal ? '0.7' : '0.4';
      }

      // Update center dot border + background color
      if (centerDotRef.current) {
        centerDotRef.current.style.borderColor = ringColor;
        centerDotRef.current.style.backgroundColor = isTeal ? ringColor : 'white';
      }

      // Update dwell progress ring
      if (progressRingRef.current) {
        progressRingRef.current.style.stroke = ringColor;
        const circumference = 2 * Math.PI * 22; // r=22
        const dashArray = state.dwellProgress * circumference;
        progressRingRef.current.style.strokeDasharray = `${dashArray} ${circumference}`;
        
        // Add glow effect when dwelling
        if (state.dwellProgress > 0) {
          progressRingRef.current.style.filter = `drop-shadow(0 0 8px ${ringColor})`;
        } else {
          progressRingRef.current.style.filter = 'none';
        }
      }

      animationFrameRef.current = requestAnimationFrame(renderLoop);
    };

    // Start render loop
    animationFrameRef.current = requestAnimationFrame(renderLoop);

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [isTrackingMethod]);

  // Listen for tracking pause state changes
  useEffect(() => {
    setIsPaused(!isTrackingActive);
  }, [isTrackingActive]);

  if (!isTrackingMethod) return null;

  const cursorSize = 56; // w-14 h-14

  return (
    <div
      ref={containerRef}
      className="fixed pointer-events-none z-[999999]"
      style={{
        // Initial position from store
        left: CursorStore.getState().x,
        top: CursorStore.getState().y,
        transform: 'translate(-50%, -50%)',
        // Use will-change for GPU acceleration
        willChange: 'left, top',
      }}
    >
      <div className="relative w-14 h-14 flex items-center justify-center transition-transform duration-150">
        {/* Dwell progress ring */}
        <svg className="absolute inset-0 w-full h-full -rotate-90" viewBox="0 0 56 56">
          {/* Background ring */}
          <circle
            ref={bgRingRef}
            cx="28"
            cy="28"
            r="22"
            fill="none"
            stroke="hsl(var(--border))"
            strokeWidth="6"
            opacity="0.4"
          />
          {/* Progress ring - orange fill */}
          <circle
            ref={progressRingRef}
            cx="28"
            cy="28"
            r="22"
            fill="none"
            stroke="hsl(var(--accent))"
            strokeWidth="6"
            strokeLinecap="round"
            strokeDasharray="0 138"
            style={{ transition: 'stroke-dasharray 75ms linear' }}
          />
        </svg>
        {/* Center dot */}
        <div 
          ref={centerDotRef}
          className="w-4 h-4 rounded-full bg-white border-2 border-accent shadow-lg"
          style={{
            boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
          }}
        />
      </div>

      {/* Pause indicator */}
      {isPaused && (
        <div className="absolute -bottom-8 left-1/2 -translate-x-1/2 whitespace-nowrap">
          <span className="text-xs bg-black/70 text-white px-2 py-1 rounded">
            מעקב מושהה
          </span>
        </div>
      )}
    </div>
  );
}
